import React, {Dispatch, SetStateAction, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {ImagePlaceholder} from "../../icons/icons/ImagePlaceholder";
import {themeColors} from "../../../styles/theme";
import {IconButton} from "../../buttons/IconButton/IconButton";
import {PencilIcon} from "../../icons/icons/PencilIcon";
import {DeleteIcon} from "../../icons/icons/DeleteIcon";
import {ProjectEntity} from "../../../api/entities";
import {useTheme} from "../../../context/themeContext";
import {formatDataTime} from "../../../utils/utils";
import {CloseIcon} from "../../icons/icons/CloseIcon";


export type ProjectsTableRowProps = {
    project: ProjectEntity;
    rowClickHandler: (projectId: string) => void;
    setSelectedProjectId: Dispatch<SetStateAction<string>>;
    setPopupVisible: Dispatch<SetStateAction<string>>;
    setProjectEditing: Dispatch<SetStateAction<ProjectEntity | null>>
}

const ProjectsTableRow = ({
                              project,
                              rowClickHandler,
                              setSelectedProjectId,
                              setPopupVisible,
                              setProjectEditing
                          }: ProjectsTableRowProps) => {
    const {t} = useTranslation();
    const {theme} = useTheme();

    const handleRowClick = useCallback((project: ProjectEntity) => {
        const {id} = project;
        rowClickHandler(id!);
    }, [rowClickHandler])

    const projectEditHandler = useCallback((event, project: ProjectEntity) => {
        event.stopPropagation();
        setProjectEditing(project);
    }, [setProjectEditing])

    const projectDeleteHandler = useCallback(async (event, id: string) => {
        event.stopPropagation();
        setSelectedProjectId(id);
        setPopupVisible('delete');
    }, [setSelectedProjectId, setPopupVisible])

    return (
        <>
            <tr
                className={`project-row ${theme}`}
                key={project.id}
                onClick={() => handleRowClick(project)}
                data-testid={`project-row_${project.id}`}
            >
                <td>
                    <div className={`project-row__title ${theme}`}>
                        <div>
                            <ImagePlaceholder
                                width={32}
                                height={32}
                                color={theme === 'light' ? themeColors.stroke : themeColors.navyBlue}
                                backgroundColor={theme === 'light' ? themeColors.grey1 : themeColors.grey3}
                            />
                        </div>
                        <p>{project.projectName}</p>
                    </div>
                </td>
                <td>
                    <div className={`project-row__time center ${theme}`}>
                        <p>
                            {formatDataTime(project.updateTs!)}
                        </p>
                    </div>
                </td>
                <td>
                    <div
                        className={`project-row__simulation-state center ${theme}`}
                    >
                        {project.projectExecutionStats?.completed}
                    </div>
                </td>
                <td>
                    <div
                        className={`project-row__simulation-state center ${theme}`}
                    >
                        {
                            project.projectExecutionStats?.running ||
                            <CloseIcon width={16} height={16} color={themeColors.white}/>
                        }
                    </div>
                </td>
                <td>
                    <div className={`project-row__controls center ${theme}`}>
                        <IconButton
                            icon={PencilIcon}
                            onClick={(event) => projectEditHandler(event, project)}
                            data-testid={`edit_project_${project.id}`}
                        />
                        <IconButton
                            icon={DeleteIcon}
                            onClick={(event) => projectDeleteHandler(event, project.id!)}
                            data-testid={`delete_project_${project.id}`}
                        />
                    </div>
                </td>
                <td>
                    <div className={`project-row__empty right ${theme}`}></div>
                </td>
            </tr>
        </>
    )
}

export default ProjectsTableRow;